
import { defineComponent, watch } from 'vue';
import { useStore } from 'vuex';
import { computed } from '@vue/runtime-core';
import { Getters, miscSettingsStore, store } from '@/store';

import { chunk } from 'lodash';
import FieldRulesManager from '@/manager/2.6/FieldRulesManager';
import FieldRulesManagerV36 from '@/manager/3.6/FieldRulesManager';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import { SourceDataInitials } from '@/constants/SourceDataInitials';

export default defineComponent({
    emits: ['callback'],
    setup() {
        const store = useStore();
        return { store };
    },
    data() {
        return {
            list: [] as any,
            columnsRemainer: 0,
            columns: 2,
            timer: 0,
            stopWatchingActiveComp: null as (() => void) | null
        };
    },
    async mounted() {
        // DataGrid's applyTransferToFormForRepeatingSections() (run inside buildGridPayload()) is what
        // actually writes repeatable-section transfer values (e.g. Dwellings-0-DWELL_STYLE) - request a
        // rebuild so Map View shows correct defaults even when opened before the grid has caught up
        // (e.g. right after an MLS import). The deep watch below picks up the resulting commit.
        if (miscSettingsStore.UADVersion !== 2.6) {
            (this as any).eventBus.emit('rebuildActiveCompGrid');
        }

        this.buildList();

        // MLS import's ProcessComp cascade (field rules/repeatable-section sync) runs async and isn't
        // awaited by the importer, so the comp's data can still be arriving after this modal opens -
        // rebuild the list whenever the active comp's data changes instead of only reading it once.
        const activeCompGetter = miscSettingsStore.UADVersion === 2.6 ? Getters.CompAddresses.ActiveComp : Getters.CompAddressesV36.ActiveComp;
        this.stopWatchingActiveComp = watch(
            () => store.getters[activeCompGetter],
            () => {
                this.buildList();
            },
            { deep: true }
        );
    },
    beforeUnmount() {
        if (this.stopWatchingActiveComp) {
            this.stopWatchingActiveComp();
        }
    },
    computed: {
        isReadOnly(): boolean {
            return miscSettingsStore.UADVersion !== 2.6;
        },
    },
    methods: {
        buildList() {
            const isV26 = miscSettingsStore.UADVersion === 2.6;

            if (isV26) {
                const fieldNames26 = ['LC_LOCAT1', 'DA_CONSTQL', 'ST_SITEVW', 'AG_COND1', 'DA_DESAPL1'];
                const result = computed(() => store.getters[Getters.CompAddresses.GetAllDataPointsByFieldId](fieldNames26));
                let rows = result.value.length / this.columns;
                if (rows % 1 != 0) {
                    rows = Math.ceil(rows);
                }
                let cells = rows * this.columns;
                this.columnsRemainer = result.value.length !== cells ? cells - result.value.length : 0;
                this.list = chunk(result.value, this.columns);
                return;
            }

            // 3.6 layout: Site Influence alone, then Quality/Condition side-by-side (not repeatable),
            // then Dwelling Style rows, then View | Range rows (each repeatable, chunked among themselves).
            // Subject and Comp use different field names/collections for these rows.
            const activeComp = store.getters[Getters.CompAddressesV36.ActiveComp];
            const isSubjectComp = activeComp?.isSubject === true;

            // Quality and Condition fields for Subject and Comp
            const qualityFieldName = isSubjectComp ? 'EXT_QUAL_RATING' : 'QUALITY';
            const conditionFieldName = isSubjectComp ? 'EXT_COND_RATING' : 'CONDITION';
            const dwellingIndices: number[] = store.getters[Getters.CompAddressesV36.GetRepeatIndicesForSection]('Dwellings');
            const qualifiedQualityFieldName = isSubjectComp && dwellingIndices.length > 0
                ? `Dwellings-${dwellingIndices[0]}-${qualityFieldName}`
                : qualityFieldName;
            const qualifiedConditionFieldName = isSubjectComp && dwellingIndices.length > 0
                ? `Dwellings-${dwellingIndices[0]}-${conditionFieldName}`
                : conditionFieldName;

            // Comp's Site Influence isn't repeatable; Subject's is (SiteInfluence-N-SIT_INFL_FLATTENED).
            const siteInfluenceSection = isSubjectComp ? 'SiteInfluence' : null;
            const siteInfluenceBaseFieldName = isSubjectComp ? 'SIT_INFL_FLATTENED' : 'SITE_INFLUENCE';
            const siteInfluenceIndices: number[] = siteInfluenceSection ? store.getters[Getters.CompAddressesV36.GetRepeatIndicesForSection](siteInfluenceSection) : [];
            const siteInfluenceFieldNames = siteInfluenceIndices.length > 0
                ? siteInfluenceIndices.map((idx) => `${siteInfluenceSection}-${idx}-${siteInfluenceBaseFieldName}`)
                : [siteInfluenceBaseFieldName];

            const dwellStyleBaseFieldName = isSubjectComp ? 'STYLE' : 'DWELL_STYLE';
            const dwellStyleFieldNames = dwellingIndices.length > 0 ? dwellingIndices.map((idx) => `Dwellings-${idx}-${dwellStyleBaseFieldName}`) : [dwellStyleBaseFieldName];

            // Comp's View | Range lives under SiteViews; Subject's lives under ViewImpairment.
            const viewSection = isSubjectComp ? 'ViewImpairment' : 'SiteViews';
            const viewBaseFieldName = isSubjectComp ? 'SIT_VWIMP_VIEW_FLATTENED' : 'ST_VIEW';
            const viewIndices: number[] = store.getters[Getters.CompAddressesV36.GetRepeatIndicesForSection](viewSection);
            const viewFieldNames = viewIndices.length > 0 ? viewIndices.map((idx) => `${viewSection}-${idx}-${viewBaseFieldName}`) : [viewBaseFieldName];

            const allFieldNames = [...siteInfluenceFieldNames, qualifiedQualityFieldName, qualifiedConditionFieldName, ...dwellStyleFieldNames, ...viewFieldNames];
            const result = computed(() => store.getters[Getters.CompAddressesV36.GetAllDataPointsByFieldId](allFieldNames));
            const byFieldName = new Map<string, any>(result.value.map((field: any) => [field.fieldName, field]));

            const rows: any[] = [];

            const buildChunkedRows = (names: string[]) => {
                const items = names
                    .map((fieldName, i) => {
                        const item = byFieldName.get(fieldName);
                        if (item && names.length > 1) {
                            item.friendlyFieldName = `${item.friendlyFieldName} ${i + 1}`;
                        }
                        return item;
                    })
                    .filter(Boolean);
                for (let i = 0; i < items.length; i += this.columns) {
                    rows.push(items.slice(i, i + this.columns));
                }
            };

            // Site Influence: single row when not repeatable (Comp), chunked rows when repeatable (Subject).
            if (siteInfluenceIndices.length > 0) {
                buildChunkedRows(siteInfluenceFieldNames);
            } else {
                const siteInfluence = byFieldName.get(siteInfluenceFieldNames[0]);
                if (siteInfluence) {
                    rows.push([siteInfluence]);
                }
            }

            const quality = byFieldName.get(qualifiedQualityFieldName);
            const condition = byFieldName.get(qualifiedConditionFieldName);
            const qualityConditionPair = [quality, condition].filter(Boolean);
            if (qualityConditionPair.length > 0) {
                rows.push(qualityConditionPair);
            }

            buildChunkedRows(dwellStyleFieldNames);
            buildChunkedRows(viewFieldNames);

            this.list = rows;
            this.columnsRemainer = this.columns - 1;
        },
        callback() {
            let result = [];
            for (const idx1 in this.list) {
                for (const idx2 in this.list[idx1]) {
                    result.push({
                        fieldId: this.list[idx1][idx2]['fieldId'],
                        fieldName: this.list[idx1][idx2]['fieldName'],
                        fieldRulesApplied: this.list[idx1][idx2]['selectedData']['fieldRulesApplied'],
                        sourceInitial: this.list[idx1][idx2]['selectedData']['sourceInitial'],
                        sourceInitialIdx: this.list[idx1][idx2]['selectedData']['sourceInitialIdx'],
                        value: this.list[idx1][idx2]['selectedData']['value']
                    });
                }
            }

            this.$emit('callback', result);
        },
        selectValue(fieldId: number, fieldName: string, item: any) {
            for (const idx1 in this.list) {
                const idx2 = this.list[idx1].findIndex((f: any) => f.fieldName === fieldName);
                if (idx2 !== -1) {
                    // -2 marks this as an explicit manual selection (matches GridRowCell's main-column
                    // click sentinel), so the grid's auto source-priority lock-in correction doesn't
                    // silently revert it back to a higher-priority feed on the next grid rebuild.
                    this.applyRules(fieldId, item.value, item.sourceInitial, idx1, idx2, -2);
                    break;
                }
            }
        },
        typedValue(fieldId: number, fieldName: string, value: string) {
            if (this.isReadOnly) {
                return;
            }
            clearTimeout(this.timer);
            this.timer = window.setTimeout(() => {
                for (const idx1 in this.list) {
                    const idx2 = this.list[idx1].findIndex((f: any) => f.fieldName === fieldName);
                    if (idx2 !== -1) {
                        this.applyRules(fieldId, value, SourceDataInitials.UserOverride, idx1, idx2, undefined);
                        break;
                    }
                }
            }, 500);
        },
        async applyRules(fieldId: number, value: string, sourceInitial: string, idx1: string, idx2: number, sourceInitialIdx: number | undefined) {
            const fieldRule: IFormFieldRule = {
                fieldId: fieldId,
                value: value
            };

            // console.log(`Dropdowns.applyRules(), Begin run field rule on active comp`);
            let fieldRules = [];
            if (miscSettingsStore.UADVersion === 2.6) {
                fieldRules = await FieldRulesManager.runFieldRule(fieldRule);
            } else {
                fieldRules = await FieldRulesManagerV36.runFieldRule(fieldRule);
            }
            
            if (fieldRules && fieldRules.length > 0) {
                this.list[idx1][idx2]['selectedData']['value'] = fieldRules[0].value;
                this.list[idx1][idx2]['selectedData']['sourceInitial'] = sourceInitial;
                this.list[idx1][idx2]['selectedData']['fieldRulesApplied'] = fieldRules[0].fieldRulesApplied;
            } else {
                // No field rule configured for this field - still apply the raw selected/typed value.
                this.list[idx1][idx2]['selectedData']['value'] = value;
                this.list[idx1][idx2]['selectedData']['sourceInitial'] = sourceInitial;
                this.list[idx1][idx2]['selectedData']['fieldRulesApplied'] = false;
            }
            this.list[idx1][idx2]['selectedData']['sourceInitialIdx'] = sourceInitialIdx;

            this.callback();
        }
    }
});
